import { Button } from "@gigpro/ui/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@gigpro/ui/Collapsible";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form, type SubmitHandler } from "@gigpro/ui/Form";
import { Select, SelectItem } from "@gigpro/ui/Form/Select";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { titleCase } from "@gigpro/utils";
import {
  type PlatformAdminLocationResponse,
  usePaymentsInvoicingCustomerRetrieve,
  useLocationsRetrieve,
  useStafferOrganizationsRetrieve,
} from "@sidegigllc/api/client";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import {
  invoiceFrequencySchema,
  invoiceStyleSchema,
  invoicingAutomationLevelSchema,
  netTermsFeeSchema,
  netTermsSchema,
  weekdaySchema,
} from "api/resources/shared";
import { useIsAdmin } from "domains/auth";
import { useFeatureFlag } from "providers/LaunchDarkly";
import React from "react";
import { useFormState, useWatch } from "react-hook-form";
import * as z from "zod/mini";
import InvoiceIcon from "./icons/invoice.svg?react";
import { InvoicedCustomerIdField } from "./InvoicedCustomerIdField";
import { StripeElementsProvider } from "./StripeElementsProvider";
import {
  type PaymentSourceId,
  isAbsoluteUrl,
  usePaymentSourceUpdate,
  useSaveStripePaymentMethod,
} from "./useSaveStripePaymentMethod";

export type StripePaymentMethodUpdateContainerProps = {
  isQwick: boolean;
  sourceId: PaymentSourceId;
  className?: string;
  returnTo: string;
  description?: string;
  onSave?: () => Promise<void>;
  renderButtons?: (props: { isSubmitting: boolean; isSaveDisabled: boolean }) => React.ReactNode;
  hideInvoiceOption?: boolean;
};

export function StripePaymentMethodUpdateContainer({
  sourceId,
  className,
  ...rest
}: Readonly<StripePaymentMethodUpdateContainerProps>) {
  return (
    <StripeElementsProvider sourceId={sourceId} className={className}>
      <UpdateForm sourceId={sourceId} {...rest} />
    </StripeElementsProvider>
  );
}

const commaEmailsSchema = z
  .optional(z.string())
  .check(
    z.refine(
      (val) => !val || val.split(",").every((e) => z.email().safeParse(e.trim()).success),
      "Each entry must be a valid email address",
    ),
  );

const FormValues = z.object({
  email: z.optional(z.email()),
  invoiceFrequency: z.optional(invoiceFrequencySchema),
  invoiceStyle: z.optional(invoiceStyleSchema),
  automationLevel: z.optional(invoicingAutomationLevelSchema),
  invoiceWorkWeekEnd: z.optional(weekdaySchema),
  netTerms: z.optional(netTermsSchema),
  netTermsFee: z.optional(netTermsFeeSchema),
  contactEmails: commaEmailsSchema,
});
type FormValues = z.infer<typeof FormValues>;

const UNGATED_AUTOMATION_LEVELS: ReadonlySet<z.infer<typeof invoicingAutomationLevelSchema>> = new Set([
  "NONE",
  "DRAFT",
]);

function UpdateForm({
  returnTo,
  onSave,
  renderButtons,
  description,
  sourceId,
  hideInvoiceOption,
}: Readonly<Omit<StripePaymentMethodUpdateContainerProps, "isQwick">>) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const isOrganization = "organizationId" in sourceId;
  const isLocation = "locationId" in sourceId;
  const isAdmin = useIsAdmin();
  const canSetCreateSend = useFeatureFlag("invoicing-automation-create-send");
  const canEditInvoicedCustomerId = useFeatureFlag("fe-invoiced-customer-id");
  const [isInvoiceOpen, setIsInvoiceOpen] = React.useState(false);
  const [stripeError, setStripeError] = React.useState<string>();

  const { data: orgData } = useStafferOrganizationsRetrieve(isOrganization ? sourceId.organizationId : "", {
    query: { enabled: isOrganization },
  });
  const { data: locationData } = useLocationsRetrieve(isLocation ? sourceId.locationId : -1, {
    query: { enabled: isLocation },
  });

  // orgData (generated Organization) lacks the invoice_* fields the org spec still returns;
  // cast to the location shape, which carries all the invoice defaults the form reads.
  const invoiceDefaults = (isOrganization ? orgData : locationData) as PlatformAdminLocationResponse | undefined;

  const { data: invoicingCustomer } = usePaymentsInvoicingCustomerRetrieve({
    location_id: isLocation ? sourceId.locationId : undefined,
    organization_id: isOrganization ? sourceId.organizationId : undefined,
  });

  const [isElementsLoading, setIsElementsLoading] = React.useState(true);
  const [stripeElementData, setStripeElementData] = React.useState<StripePaymentElementChangeEvent>();
  const isCreditCard = stripeElementData?.value.type === "card" && !stripeElementData?.collapsed && !isInvoiceOpen;
  const isLoading = !stripe || !elements || isElementsLoading;

  const form = useZodForm({
    schema: FormValues,
    schemaTransform: (schema) => (isCreditCard ? z.required(schema) : schema),
    defaultValues: {
      invoiceFrequency: invoiceDefaults?.invoice_frequency ?? "WEEKLY",
      invoiceStyle: invoiceDefaults?.invoice_style ?? "STANDARD",
      automationLevel: invoiceDefaults?.invoicing_automation_level ?? "NONE",
      invoiceWorkWeekEnd: invoiceDefaults?.invoice_work_week_end_date ?? "MONDAY",
      netTerms: invoiceDefaults?.invoice_net_terms ?? "NET_30",
      netTermsFee: invoiceDefaults?.invoice_net_terms_fee ?? "0%",
      contactEmails: invoiceDefaults?.invoice_contact_emails?.join(", ") ?? "",
    },
  });
  const { control, handleSubmit } = form;
  const { isSubmitting } = useFormState({ control });

  React.useEffect(() => {
    if (invoicingCustomer) {
      form.reset({
        ...form.getValues(),
        netTerms: invoicingCustomer.net_terms ?? undefined,
        netTermsFee: invoicingCustomer.net_terms_fee ?? undefined,
        contactEmails: invoicingCustomer.emails?.join(", ") ?? "",
      });
    }
  }, [form, invoicingCustomer]);

  // `defaultValues` is captured on the first render, when `invoiceDefaults` is still in flight on
  // a cold query cache — so the select would sit on "NONE" for an entity actually stored as, say,
  // SEND. This field is part of the PATCH payload, so that stale default does not just display
  // wrong, it gets written back: an unrelated edit would silently downgrade SEND to NONE. Seed it
  // from the entity instead. Scoped to this one field on purpose; the sibling selects have the
  // same staleness but are out of scope here (tracked separately).
  const storedAutomationLevel = invoiceDefaults?.invoicing_automation_level;

  // "The admin picked this themselves" has to be tracked apart from react-hook-form's dirty
  // state, because dirty is measured against a baseline the form rebuilds: the `form.reset` above
  // reinstates the current values as the new defaults, so a pick made while both queries were
  // still in flight stops reading as dirty the moment the invoicing-customer query lands. The
  // seeding below and the payload further down both hang off this answer, so it has to survive
  // that reset — a ref does, form state does not. RHF tags user-originated updates `type:
  // "change"`; `setValue` and `reset` arrive untyped, so this never mistakes our own writes for
  // the admin's.
  const hasEditedAutomationLevel = React.useRef(false);
  React.useEffect(() => {
    const subscription = form.watch((_values, { name, type }) => {
      if (name === "automationLevel" && type === "change") hasEditedAutomationLevel.current = true;
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Seeding follows the stored value rather than firing once, because the entity query answers
  // more than once for a single open form: the query client keeps a 10s `staleTime` and leaves
  // `refetchOnWindowFocus` at its default, so tabbing away and back re-reads the entity. If the
  // level was changed elsewhere in the meantime, a one-shot seed leaves the select showing the
  // value from the *first* response — and `storedAutomationLevel` is truthy by then, so the
  // payload guard below waves that stale value straight through and an unrelated save PATCHes it
  // back over the new one. Tracking the last value seeded closes that: a genuine change re-seeds,
  // while a repeat of the same value (every other refetch) is a no-op.
  const seededAutomationLevel = React.useRef<z.infer<typeof invoicingAutomationLevelSchema> | undefined>(undefined);
  React.useEffect(() => {
    if (!storedAutomationLevel || storedAutomationLevel === seededAutomationLevel.current) return;
    seededAutomationLevel.current = storedAutomationLevel;
    // Never stomp a choice the admin has already made in an open form — seeding a field they
    // have touched would trade a silent downgrade for a silently reverted edit. Recorded above
    // regardless, so this stays a decision about one specific stored value, not a latch.
    if (hasEditedAutomationLevel.current) return;
    form.setValue("automationLevel", storedAutomationLevel);
  }, [form, storedAutomationLevel]);

  // Gate what is RENDERED, never what is SUBMITTED. CREATE and SEND are acted on by the daily
  // invoicing batch — CREATE opens a real invoice on Invoiced.com, SEND emails it to the customer
  // — so until that pipeline is trusted neither is pickable. An entity already stored as one of
  // them still has to read back correctly though, and Radix renders the trigger blank when the
  // selected value matches no item, so keep that one value in the list as a disabled option.
  // Coercing it out of the form value instead would downgrade the stored level on an unrelated
  // save, which is the write-back regression fixed in f24d5edbb — hence the split.
  const selectedAutomationLevel = useWatch({ control, name: "automationLevel" });
  const isGatedAutomationLevel = (level: z.infer<typeof invoicingAutomationLevelSchema>) =>
    !canSetCreateSend && !UNGATED_AUTOMATION_LEVELS.has(level);
  const automationLevelOptions = invoicingAutomationLevelSchema.options.filter(
    (level) => !isGatedAutomationLevel(level) || level === selectedAutomationLevel,
  );

  const isEdited = !stripeElementData?.empty;
  const preventNavigation = isEdited && !isSubmitting;
  const blocker = useBlocker({
    shouldBlockFn: () => preventNavigation,
    enableBeforeUnload: preventNavigation,
    withResolver: true,
  });

  const updatePaymentSource = usePaymentSourceUpdate(sourceId);
  const saveStripePaymentMethod = useSaveStripePaymentMethod(sourceId);

  const onSubmit: SubmitHandler<FormValues> = async ({
    email,
    invoiceFrequency,
    invoiceStyle,
    automationLevel,
    invoiceWorkWeekEnd,
    netTerms,
    netTermsFee,
    contactEmails,
  }) => {
    if (isLoading) return;

    // Only send the automation level when the value sitting in the form is one we can stand
    // behind: the admin picked it by hand, or the entity query answered and seeded the stored
    // one. Before either, the form still holds the `?? "NONE"` fallback `defaultValues` captured
    // on the first render — a slow or failed retrieve leaves it there — and PATCHing that writes
    // NONE over a stored SEND, switching that customer's invoicing off with nothing on screen to
    // say so. Omitting the key instead leaves the stored value untouched, exactly the way the
    // non-admin case below degrades.
    const hasTrustworthyAutomationLevel = hasEditedAutomationLevel.current || !!storedAutomationLevel;

    // these need to be sequential
    await onSave?.();
    try {
      if (!isInvoiceOpen) {
        const { error } = await saveStripePaymentMethod.save({ email, returnUrl: returnTo });
        if (error) {
          if (error.type !== "validation_error") setStripeError(error.message);
          return;
        }
      } else {
        await updatePaymentSource({
          payment_source: "INVOICING",
          invoice_frequency: invoiceFrequency,
          invoice_style: invoiceStyle,
          // Admin-only field, and only once it has a value worth writing: omit the key
          // entirely for non-admins, and for anyone submitting before the stored level has
          // hydrated, so neither save can clobber it.
          ...(isAdmin && hasTrustworthyAutomationLevel && { invoicing_automation_level: automationLevel }),
          invoice_work_week_end_date: invoiceWorkWeekEnd,
          invoice_net_terms: netTerms,
          invoice_net_terms_fee: netTermsFee,
          invoice_contact_emails: contactEmails ? contactEmails.split(",").map((e) => e.trim()) : [],
        });
      }
    } catch {
      // Only the PATCH rejects (confirm failures come back as `error`), and usePaymentSourceUpdate toasts it.
      return;
    }
    if (isAbsoluteUrl(returnTo)) {
      globalThis.location.href = returnTo;
    } else {
      void navigate({ href: returnTo });
    }
  };

  return (
    <>
      <Dialog open={blocker.status === "blocked"} onClose={() => blocker.status === "blocked" && blocker.reset()}>
        <DialogTitle>You have unsaved changes</DialogTitle>
        <p className="text-normal"> Do you want to save the current information first?</p>
        <DialogFooter>
          <Button size="small" className="w-full" onClick={() => blocker.status === "blocked" && blocker.proceed()}>
            Leave without saving
          </Button>
          <Button
            size="small"
            variant="contained"
            className="w-full"
            onClick={() => {
              if (blocker.status === "blocked") blocker.reset();
              void handleSubmit(onSubmit)();
            }}
          >
            Save changes
          </Button>
        </DialogFooter>
      </Dialog>
      <Form form={form} onSubmit={onSubmit}>
        {isLoading && (
          <div className="flex w-full justify-center">
            <div className="loading-spinner size-8 text-brand" />
          </div>
        )}
        {!isLoading && typeof description === "string" && <p className="pb-2">{description}</p>}
        <Collapsible open={!isElementsLoading && isCreditCard}>
          <CollapsibleContent>
            <Textfield
              type="email"
              control={control}
              name="email"
              shouldUnregister
              label="Receipt email"
              placeholder="jane@gmail.com"
              required
              className="mb-3"
            />
          </CollapsibleContent>
        </Collapsible>
        {!hideInvoiceOption && (
          <div className="mb-3">
            <p className="font-semibold">
              Set the default payment method for this {isLocation ? "location" : "organization"}
            </p>
            {isOrganization && (
              <p>
                New locations use the organization's default payment method unless a custom one is set for that
                location.
              </p>
            )}
          </div>
        )}
        {!hideInvoiceOption ? (
          <div>
            <Collapsible open={isInvoiceOpen} onOpenChange={(open) => open && setIsInvoiceOpen(true)} className="mb-2">
              <CollapsibleTrigger
                className={`group flex w-full items-center gap-3 border text-left outline-none ${isInvoiceOpen ? "border-b-0" : "cursor-pointer"}`}
                style={{
                  borderColor: "var(--color-light-gray)",
                  borderRadius: isInvoiceOpen ? "4px 4px 0 0" : "4px",
                  padding: "12px",
                  fontFamily: "var(--font-sans)",
                  fontSize: "14px",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  color: "var(--color-black)",
                }}
                onClick={() => {
                  if (!isInvoiceOpen) {
                    elements?.getElement("payment")?.collapse();
                  }
                }}
              >
                <span
                  className="ml-1 flex size-3 shrink-0 items-center justify-center rounded-full border-2"
                  style={{ borderColor: isInvoiceOpen ? "var(--color-brand)" : "var(--color-gray)" }}
                >
                  {isInvoiceOpen && (
                    <span className="size-1.5 rounded-full" style={{ backgroundColor: "var(--color-brand)" }} />
                  )}
                </span>
                <InvoiceIcon
                  className={`-ml-1 size-4 shrink-0 ${isInvoiceOpen ? "text-brand" : "text-[#545661] group-hover:text-black"}`}
                />
                <span className={`ml-1.5 ${isInvoiceOpen ? "text-brand" : "text-[#545661] group-hover:text-black"}`}>
                  Invoice
                </span>
                {!isAdmin && isInvoiceOpen && (
                  <span className="ml-auto max-w-[95%] text-right text-extraSmall font-regular text-[#9CA3AF]">
                    To set your payment method to invoicing or to change your invoice settings, please contact support.
                  </span>
                )}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div
                  className="flex flex-col gap-3 rounded-b border border-t-0 p-3"
                  style={{ borderColor: "var(--color-light-gray)" }}
                >
                  <Select control={control} name="invoiceFrequency" label="Invoice frequency" disabled={!isAdmin}>
                    {invoiceFrequencySchema.options.map((freq) => (
                      <SelectItem key={freq} value={freq}>
                        {titleCase(freq)}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select control={control} name="invoiceStyle" label="Invoice style" disabled={!isAdmin}>
                    {invoiceStyleSchema.options.map((style) => (
                      <SelectItem key={style} value={style}>
                        {titleCase(style)}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select control={control} name="invoiceWorkWeekEnd" label="Invoice work week end" disabled={!isAdmin}>
                    {weekdaySchema.options.map((weekday) => (
                      <SelectItem key={weekday} value={weekday}>
                        {titleCase(weekday)}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select control={control} name="netTerms" label="Net terms" disabled={!isAdmin}>
                    {netTermsSchema.options.map((term) => (
                      <SelectItem key={term} value={term}>
                        {term === "AUTOPAY" ? "Autopay" : term.replace("_", " ")}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select control={control} name="netTermsFee" label="Net terms fee" disabled={!isAdmin}>
                    {netTermsFeeSchema.options.map((fee) => (
                      <SelectItem key={fee} value={fee}>
                        {fee}
                      </SelectItem>
                    ))}
                  </Select>
                  {isAdmin && (
                    <Select control={control} name="automationLevel" label="Automation level">
                      {automationLevelOptions.map((level) => (
                        <SelectItem key={level} value={level} disabled={isGatedAutomationLevel(level)}>
                          {titleCase(level)}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                  {isAdmin && canEditInvoicedCustomerId && (
                    <InvoicedCustomerIdField sourceId={sourceId} savedId={invoiceDefaults?.invoiced_customer_id} />
                  )}
                  <Textfield
                    control={control}
                    name="contactEmails"
                    label="Contact email(s)"
                    placeholder="jane@example.com, john@example.com"
                    disabled={!isAdmin}
                    className={
                      !isAdmin ? "opacity-50 [&_div]:bg-white [&_input]:cursor-not-allowed [&_input]:bg-white" : ""
                    }
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
            <PaymentElement
              options={{
                layout: { type: "accordion", defaultCollapsed: true, radios: "always", spacedAccordionItems: true },
                readOnly: saveStripePaymentMethod.isConfirmed,
              }}
              onReady={() => setIsElementsLoading(false)}
              onChange={(event) => {
                setStripeElementData(event);
                if (!event.collapsed) {
                  setIsInvoiceOpen(false);
                }
              }}
            />
          </div>
        ) : (
          <PaymentElement
            options={{ readOnly: saveStripePaymentMethod.isConfirmed }}
            onReady={() => setIsElementsLoading(false)}
            onChange={(event) => setStripeElementData(event)}
          />
        )}
        <p className="mt-4 text-normal text-error">{stripeError}</p>
        {!isLoading &&
          (renderButtons ? (
            renderButtons({ isSubmitting, isSaveDisabled: isInvoiceOpen && !isAdmin })
          ) : (
            <div className="mt-8 flex flex-col gap-2 sm:flex-row">
              <Button className="w-full" disabled={isSubmitting} onClick={() => navigate({ href: returnTo })}>
                Cancel
              </Button>
              <Button
                className="w-full"
                variant="contained"
                type="submit"
                loading={isSubmitting}
                disabled={isInvoiceOpen && !isAdmin}
              >
                Save Payment Method
              </Button>
            </div>
          ))}
      </Form>
    </>
  );
}
